import {isEqual} from 'lodash';

import {AppAction} from '../../../appActions';
import {hasSearchInInboxesParameters} from '../../inboxes/parameters/inboxesParameters';
import {CellsPanelActionsEnum} from '../cellsPanelActions';
import {
  CellsPanelSearchActionsEnum,
  CellsPanelSearchHintsActionsEnum,
  CellsPanelSearchSessionTimestampActionsEnum,
  CellsPanelSearchSuggestionsActionsEnum,
} from './cellsPanelSearchActions';
import {CellsPanelSearchState, DEFAULT_SEARCH_RESULTS_COUNTS} from './cellsPanelSearchState';

export function cellsPanelSearchReducer(state: CellsPanelSearchState, action: AppAction) {
  switch (action.type) {
    case CellsPanelSearchActionsEnum.SET_RESULTS_COUNT:
      return {
        ...state,
        resultsCounts: {
          ...state.resultsCounts,
          [action.payload.location]: action.payload.count,
        },
      };

    // We cannot reset counts with CellsPanelActionsEnum.NAVIGATED because we have different
    // behaviors on desktop and mobile.
    // To make sure counts are correctly reset, we need to dispatch a specific action.
    case CellsPanelSearchActionsEnum.RESET_RESULTS_COUNTS:
      return {
        ...state,
        resultsCounts: DEFAULT_SEARCH_RESULTS_COUNTS,
      };

    case CellsPanelSearchActionsEnum.SAVE_RECENT_QUERIES:
      return {
        ...state,
        recentQueries: action.payload,
      };

    case CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS:
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          isLoading: true,
        },
      };

    case CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_COMPLETED:
      return {
        ...state,
        suggestions: {
          isLoading: false,
          results: action.payload.results,
        },
      };

    case CellsPanelSearchSuggestionsActionsEnum.FETCH_SUGGESTIONS_FAILED:
      return {
        ...state,
        suggestions: {
          isLoading: false,
          results: [],
        },
      };

    case CellsPanelSearchActionsEnum.CLEAR_SUGGESTIONS:
      return {
        ...state,
        suggestions: {
          isLoading: false,
          results: [],
        },
      };

    case CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS:
      return {
        ...state,
        hints: {
          ...state.hints,
          isLoading: true,
        },
      };

    case CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_COMPLETED: {
      const {
        hints: {results: previousResults = []},
      } = state;

      const results = action.payload.hints.map((hint) => {
        const maybePreviousResult = previousResults?.find(
          (h) => h.type === hint.type && isEqual(h.values, hint.values),
        );

        // If hint already exists, keep its former transience.
        if (maybePreviousResult) {
          return {
            ...hint,
            isTransient: maybePreviousResult.isTransient ?? hint.isTransient,
          };
        }

        return hint;
      });

      return {
        ...state,
        hints: {
          isLoading: false,
          results,
        },
      };
    }

    case CellsPanelSearchHintsActionsEnum.FETCH_SEARCH_HINTS_FAILED:
    case CellsPanelSearchActionsEnum.CLEAR_SEARCH_HINTS:
      return {
        ...state,
        hints: {
          isLoading: false,
          results: null,
        },
      };

    case CellsPanelSearchSessionTimestampActionsEnum.UPDATE_SEARCH_SESSION_TIMESTAMP:
      return {
        ...state,
        searchSessionTimestamp: action.payload.timestamp,
      };

    case CellsPanelSearchActionsEnum.SET_HIGHLIGHT_ALGORITHM:
      return {
        ...state,
        highlightAlgorithm: action.payload,
      };

    case CellsPanelActionsEnum.NAVIGATED: {
      // If we're navigating away from search and search session timestamp is defined, clear the search session timestamp.
      if (state.searchSessionTimestamp && !hasSearchInInboxesParameters(action.payload.page.parameters)) {
        return {
          ...state,
          searchSessionTimestamp: null,
        };
      }

      return state;
    }

    default:
      return state;
  }
}
